import { getDoc, updateDoc } from 'firebase/firestore';

/**
 * Normalize venue screen / venue doc data into a Tour Planner tour stop (folder.tour.days[].venues).
 */
export function venueDataToTourStop(venueData) {
  const id = venueData?.id;
  if (!id) return null;
  return {
    id,
    name: venueData.name || '',
    city: venueData.city || '',
    state: venueData.state || '',
    latitude: venueData.latitude ?? null,
    longitude: venueData.longitude ?? null,
    capacity: venueData.capacity || '',
    venueType: venueData.venueType || '',
    ...(venueData.backgroundImage ? { backgroundImage: venueData.backgroundImage } : {}),
  };
}

/**
 * Rebuild `savedVenue` file rows for a tour folder from `tour.days` (same pattern as TourPlanner.saveTour).
 */
export function syncSavedVenueFilesForTourFolder(folderId, tourDays, existingFiles) {
  const updatedFiles = { ...existingFiles };
  for (const [fileId, file] of Object.entries(updatedFiles)) {
    if (file && file.parentId === folderId && file.itemType === 'savedVenue') {
      delete updatedFiles[fileId];
    }
  }
  const tourVenues = (tourDays || []).flatMap((d) => d.venues || []);
  const seenIds = new Set();
  for (const venue of tourVenues) {
    if (!venue || !venue.id || seenIds.has(venue.id)) continue;
    seenIds.add(venue.id);
    const fileId = `${folderId}_${venue.id}`;
    updatedFiles[fileId] = {
      parentId: folderId,
      itemType: 'savedVenue',
      data: {
        id: venue.id,
        name: venue.name || '',
        city: venue.city || '',
        state: venue.state || '',
        latitude: venue.latitude ?? null,
        longitude: venue.longitude ?? null,
        capacity: venue.capacity || '',
        venueType: venue.venueType || '',
      },
    };
  }
  return updatedFiles;
}

/**
 * First-time tour metadata on a folder (e.g. "New Tour" from venue screen under My Tours).
 */
export function createInitialTourFolderRecord(folder, venueStop) {
  const name = String(folder.name || 'My Tour').trim() || 'My Tour';
  return {
    ...folder,
    tour: {
      name,
      days: [
        {
          id: 'day-1',
          name: 'Day 1',
          venues: [venueStop],
          legModes: [],
        },
      ],
      interDayLegModes: [],
      eventStartDate: null,
      eventEndDate: null,
      linkedPostId: null,
      linkedPostSummary: null,
    },
  };
}

/**
 * Append a stop to the last day; extend within-day legModes like TourPlanner.addVenueToDay.
 */
export function appendVenueToLastTourDay(folder, venueStop) {
  const tour = folder.tour;
  if (!tour || !Array.isArray(tour.days)) {
    return createInitialTourFolderRecord(folder, venueStop);
  }
  const days = tour.days.map((d) => ({
    ...d,
    venues: [...(d.venues || [])],
    legModes: [...(d.legModes || [])],
  }));
  if (days.length === 0) {
    return createInitialTourFolderRecord(folder, venueStop);
  }
  const lastIdx = days.length - 1;
  const lastDay = { ...days[lastIdx] };
  const venues = [...(lastDay.venues || []), venueStop];
  lastDay.venues = venues;
  const maxLegs = Math.max(0, venues.length - 1);
  const prevLegs = lastDay.legModes || [];
  lastDay.legModes = Array.from({ length: maxLegs }, (_, i) => prevLegs[i] || 'drive');
  days[lastIdx] = lastDay;

  const nInter = Math.max(0, days.length - 1);
  const inter = Array.isArray(tour.interDayLegModes) ? [...tour.interDayLegModes] : [];
  while (inter.length < nInter) inter.push('drive');
  if (inter.length > nInter) inter.length = nInter;

  return {
    ...folder,
    tour: {
      ...tour,
      days,
      interDayLegModes: inter,
    },
  };
}

/**
 * Merge a venue into a My Tours folder: initializes `folder.tour` for plain folders or appends to the last day
 * when `folder.tour` exists. Syncs savedVenue file rows like TourPlanner.saveTour.
 *
 * @returns {{ folders: object, files: object }}
 */
export async function applyVenueToTourFolderInUserLibrary(userRef, folderId, venueData) {
  const venueStop = venueDataToTourStop(venueData);
  if (!venueStop) throw new Error('Venue id required');

  const snap = await getDoc(userRef);
  const data = snap.data() || {};
  const folders = { ...(data.folders || {}) };
  const existingFolder = folders[folderId];
  if (!existingFolder) throw new Error('Folder not found');

  const nextFolder =
    existingFolder.tour && Array.isArray(existingFolder.tour.days)
      ? appendVenueToLastTourDay(existingFolder, venueStop)
      : createInitialTourFolderRecord(existingFolder, venueStop);

  folders[folderId] = nextFolder;
  const files = syncSavedVenueFilesForTourFolder(folderId, nextFolder.tour.days, data.files || {});

  await updateDoc(userRef, { folders, files });
  return { folders, files };
}
